import { useLayoutEffect, useRef, useState } from 'react'

export type DropdownPosition = {
  top: number
  left: number
  width: number
  isOpenAbove: boolean
}

const DROPDOWN_HEIGHT_ESTIMATE = 240

export function useDropdownPosition(
  shouldShow: boolean,
  inputRef: React.RefObject<HTMLElement | null>,
  maxDropdownHeight?: string | number
): DropdownPosition | null {
  const [pos, setPos] = useState<DropdownPosition | null>(null)
  const rafRef = useRef(0)

  useLayoutEffect(() => {
    if (!shouldShow) return

    const update = () => {
      const el = inputRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const dropdownHeight =
        typeof maxDropdownHeight === 'number'
          ? maxDropdownHeight
          : DROPDOWN_HEIGHT_ESTIMATE
      const spaceBelow = window.innerHeight - rect.bottom
      const isOpenAbove = spaceBelow < dropdownHeight && rect.top > spaceBelow
      setPos({
        top: isOpenAbove
          ? rect.top + window.scrollY
          : rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        isOpenAbove,
      })
    }

    const scheduleUpdate = () => {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', scheduleUpdate, true)
    window.addEventListener('resize', scheduleUpdate)

    const ro = new ResizeObserver(scheduleUpdate)
    const el = inputRef.current
    if (el) ro.observe(el)

    return () => {
      cancelAnimationFrame(rafRef.current)
      window.removeEventListener('scroll', scheduleUpdate, true)
      window.removeEventListener('resize', scheduleUpdate)
      ro.disconnect()
    }
  }, [shouldShow, inputRef, maxDropdownHeight])

  return pos
}
